import ControlViewModel from '@/ViewModels/ControlViewModel';
import PropertyGroupBoxViewModel from '@/ViewModels/Controls/PropertyGroupBoxViewModel';
import PropertyGroupBoxViewModelT from '@/ViewModels/Controls/PropertyGroupBoxViewModel';
import ComponentManager from '@/Management/ComponentManager';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import DetailViewContext from "@/ViewModels/Contexts/DetailViewContext";
import { IControlViewModelConstructorParameter } from "#/CoreFrameInterfaces";
import LDAPUsersFactory from "@/Factories/ControlFactories/LDAPUsersFactory";
import { IDetailViewGroupDTO, IDVGItemDTO } from "coreframetypings/dto";
import Factory from "@/Factories/Factory";

export interface ILDAPSearchViewModelConstructorParameters extends IControlViewModelConstructorParameter {
    context?: DetailViewContext;
}

export default class LDAPSearchViewModel extends ControlViewModel {
    /**
     * Der aktuelle DetailViewContext
     */
    public context: DetailViewContext;

    /**
     * Die PropertyGroupBox welche alle inneren Controls erzeugt
     */
    public propertyGroupBox: PropertyGroupBoxViewModel;

    /**
     * Der Text des Buttons zum Importieren
     */
    public importText: string;

    /**
     * Der Text des Buttons zum Importieren der Named Users
     */
    public importNamedUsersText: string;

    /**
     * Der Text des Buttons zum Importieren der Light Users
     */
    public importLightUsersText: string;

    /**
     * Der Tooltip des Buttons zum Importieren
     */
    public importTooltip: string;

    /**
     * Der Tooltip des Buttons zum Importieren der Named Users
     */
    public importNamedUsersTooltip: string;

    /**
     * Der Tooltip des Buttons zum Importieren der Light Users
     */
    public importLightUsersTooltip: string;

    private PropertyGroupBoxViewModel = ComponentManager.getViewModel<typeof PropertyGroupBoxViewModelT>('PropertyGroupBoxViewModel');

    constructor(params: ILDAPSearchViewModelConstructorParameters) {
        super(params);

        this.context = this.paramOrDefault(params.context, null);
        this.undisposeProperties.push('context');

        this.importText = 'LDAP_IMPORT';
        this.importNamedUsersText = 'LDAP_IMPORTNAMEDUSERS';
        this.importLightUsersText = 'LDAP_IMPORTLIGHTUSERS';

        this.importTooltip = 'LDAP_IMPORT_TOOLTIP';
        this.importNamedUsersTooltip = 'LDAP_IMPORTNAMEDUSERS_TOOLTIP';
        this.importLightUsersTooltip = 'LDAP_IMPORTLIGHTUSERS_TOOLTIP';

        this.propertyGroupBox = this.createPropertyGroupBox();
    }

    /**
     * Ein Handler für den Importvorgang
     */
    public import(mode?: 'named' | 'light') {
        this.context.getUIFactory<LDAPUsersFactory>('LDAPUsers').viewModel.import(mode);
    }

    protected createPropertyGroupBox(): PropertyGroupBoxViewModelT {
        return new this.PropertyGroupBoxViewModel({
            context: this.context,
            detailViewGroups: [
                this.createLicenseInformationExpander(),
                this.createSearchExpander(),
                this.createResultExpander(),
            ]
        });
    }

    protected createLicenseInformationExpander(): IDetailViewGroupDTO {
        const items = [
            this.createItem('AvailableNamedUsers', 'LDAP_NAMEDUSERS', Factory.toUIGeneratorQuery({
                uiGenerator: 'NumericTextBox',
                parameters: {
                    'floatingPrecision': 0
                }
            }), undefined, {
                isEnabled: false,
            }),
            this.createItem('AvailableLightUsers', 'LDAP_LIGHTUSERS', Factory.toUIGeneratorQuery({
                uiGenerator: 'NumericTextBox',
                parameters: {
                    'floatingPrecision': 0,
                    'neighborOf': 'AvailableNamedUsers'
                }
            }), undefined, {
                isEnabled: false,
                isVisible: this.context.genericObject.getValue<boolean>('IsLightUserActivated'),
            }),
        ];

        for (const item of items) {
            this.context.genericObject.suppressNotificationFor(item.name);
        }

        const groupUiGenerator = Factory.toUIGeneratorQuery({
            uiGenerator: 'ExpanderGroup',
            parameters: {
                'layout': 'TwoColumn'
            }
        });

        return this.createGroup('LicenseInformation', 'LDAP_LICENSEINFORMATIONEXPANDER', groupUiGenerator, {
            items: items
        });
    }

    protected createSearchExpander(): IDetailViewGroupDTO {
        const items = [
            this.createItem('Username', 'LDAP_LOGIN', 'TextBox', 'LDAP_LOGIN_TOOLTIP'),
            this.createItem('FirstName', 'LDAP_FIRSTNAME', 'TextBox', 'LDAP_FIRSTNAME_TOOLTIP'),
            this.createItem('LastName', 'LDAP_LASTNAME', this.createUiGenerator('TextBox', 'FirstName'), 'LDAP_LASTNAME_TOOLTIP'),
            this.createItem('Company', 'LDAP_COMPANY', 'TextBox', 'LDAP_COMPANY_TOOLTIP'),
            this.createItem('Email', 'EMAIL', this.createUiGenerator('TextBox', 'Company'), 'LDAP_EMAIL_TOOLTIP'),
            this.createItem('MemberOf', 'LDAP_MEMBEROF', 'Chips', 'LDAP_MEMBEROF_TOOLTIP'),
            this.createItem('Language', 'LDAP_LANGUAGE', this.createUiGenerator('TextBox', 'MemberOf'), 'LDAP_LANGUAGE_TOOLTIP'),
            this.createItem('Extension1', 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LDAP.RADLDAPCONNECTION_EXTENSION1ATTRIBUTE_ERGNAME', 'TextBox', 'LDAP_EXTENSION1_TOOLTIP'),
            this.createItem('Extension2', 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LDAP.RADLDAPCONNECTION_EXTENSION2ATTRIBUTE_ERGNAME', this.createUiGenerator('TextBox', 'Extension1'), 'LDAP_EXTENSION2_TOOLTIP'),
            this.createItem('Extension3', 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LDAP.RADLDAPCONNECTION_EXTENSION3ATTRIBUTE_ERGNAME', 'TextBox', 'LDAP_EXTENSION3_TOOLTIP'),
        ];

        const buttonGroup = this.createGroup('buttons', undefined, 'PropertyGroup?Layout=FlowLayout(?Orientation=Horizontal&Alignment=Left)', {
            items: [
                this.createItem('LDAPUsers.Search', 'LDAP_SEARCH', 'Button', 'LDAP_SEARCH_TOOLTIP', {
                    iconUrl: GlobalConfiguration.inlineImages.MAGNIFYING_GLASS,
                }),
                this.createItem('LDAPUsers.Reset', 'RESET', 'Button', 'LDAP_RESET_TOOLTIP', {
                    iconUrl: GlobalConfiguration.inlineImages.RESET,
                }),
            ]
        });

        for (const item of items.concat(buttonGroup.items)) {
            this.context.genericObject.suppressNotificationFor(item.name);
        }

        const groupUiGenerator = Factory.toUIGeneratorQuery({
            uiGenerator: 'ExpanderGroup',
            parameters: {
                'IsVisible': true,
                'Layout': 'TwoColumn',
            }
        });
        return this.createGroup('Search', 'LDAP_SEARCHEXPANDER', groupUiGenerator, {
            items: items,
            detailViewGroups: [buttonGroup]
        });
    }

    protected createResultExpander(): IDetailViewGroupDTO {
        return this.createGroup('Results', 'LDAP_RESULTEXPANDER', 'ExpanderGroup?IsVisible=true', {
            items: [
                this.createItem('LDAPUsers', 'LDAPUsers', 'LDAPUsers', undefined),
            ]
        });
    }

    protected createGroup(name: string, ergName: string, uiGenerator: string, additionalParameters: Partial<IDetailViewGroupDTO> = {}): IDetailViewGroupDTO {
        return {
            name,
            ergName,
            uiGenerator,
            ...additionalParameters
        } as IDetailViewGroupDTO;
    }

    protected createItem(name: string, ergName: string, uiGenerator: string, tooltip?: string, additionalParameters: Partial<IDVGItemDTO> = {}): IDVGItemDTO {
        return {
            name,
            ergName,
            uiGenerator,
            tooltip,
            guiReadOnly: false,
            isDerived: false,
            isDerivedOverwritable: false,
            required: false,
            ...additionalParameters
        } as IDVGItemDTO;
    }

    protected createUiGenerator(name: string, neighbor: string): string {
        return Factory.toUIGeneratorQuery({
            uiGenerator: name,
            parameters: {
                'neighborOf': neighbor
            }
        });
    }
}

ComponentManager.setViewModel('LDAPSearchViewModel', LDAPSearchViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');